<template>
  <div class="add-horse-page">
    <!-- Header with Back Button -->
    <div class="page-header">
      <el-button link class="back-btn" @click="goBack">
        <el-icon><ArrowLeft /></el-icon>
        {{ $t('common.back') }}
      </el-button>
      <h1 class="page-title">{{ $t('horse.addTitle') }}</h1>
    </div>

    <!-- Form Card -->
    <el-card class="form-card" shadow="never">
      <div class="card-header">
        <el-icon><Trophy /></el-icon>
        <span>{{ $t('horse.basicInfo') }}</span>
      </div>

      <el-form :model="horseForm" :rules="rules" ref="formRef" class="horse-form">
        <!-- Name -->
        <el-form-item prop="name">
          <template #label>
            <div class="form-label">
              <el-icon><Edit /></el-icon>
              <span>{{ $t('horse.name') }} <span class="required">*</span></span>
            </div>
          </template>
          <el-input 
            v-model="horseForm.name" 
            :placeholder="$t('horse.namePlaceholder')"
            clearable
          />
        </el-form-item>

        <!-- Breed -->
        <el-form-item prop="breed">
          <template #label>
            <div class="form-label">
              <el-icon><Flag /></el-icon>
              <span>{{ $t('horse.breed') }} <span class="required">*</span></span>
            </div>
          </template>
          <el-input 
            v-model="horseForm.breed" 
            :placeholder="$t('horse.breedPlaceholder')"
            clearable
          />
        </el-form-item>

        <!-- Color -->
        <el-form-item prop="color">
          <template #label>
            <div class="form-label">
              <el-icon><MagicStick /></el-icon>
              <span>{{ $t('horse.color') }}</span>
            </div>
          </template>
          <el-input 
            v-model="horseForm.color" 
            :placeholder="$t('horse.colorPlaceholder')"
            clearable
          />
        </el-form-item>

        <!-- Gender and Age Row -->
        <div class="form-row">
          <el-form-item prop="gender">
            <template #label>
              <div class="form-label">
                <el-icon><User /></el-icon>
                <span>{{ $t('horse.gender') }} <span class="required">*</span></span>
              </div>
            </template>
            <el-radio-group v-model="horseForm.gender">
              <el-radio :value="true">
                <el-icon><Male /></el-icon>
                {{ $t('horse.male') }}
              </el-radio>
              <el-radio :value="false">
                <el-icon><Female /></el-icon>
                {{ $t('horse.female') }}
              </el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item prop="age">
            <template #label>
              <div class="form-label">
                <el-icon><Timer /></el-icon>
                <span>{{ $t('horse.age') }} <span class="required">*</span></span>
              </div>
            </template>
            <el-input-number 
              v-model="horseForm.age" 
              :min="0" 
              :max="50" 
              :precision="0"
              style="width: 100%"
              :placeholder="$t('horse.agePlaceholder')"
              @change="handleAgeChange"
            />
          </el-form-item>
        </div>

        <!-- Birth Date -->
        <el-form-item prop="birthDate">
          <template #label>
            <div class="form-label">
              <el-icon><Calendar /></el-icon>
              <span>{{ $t('horse.birthDate') }}</span>
            </div>
          </template>
          <el-date-picker 
            v-model="horseForm.birthDate" 
            type="date" 
            :placeholder="$t('horse.birthDatePlaceholder')"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
            @change="handleBirthDateChange"
          />
        </el-form-item>

        <!-- Avatar -->
        <el-form-item prop="avatar">
          <template #label>
            <div class="form-label">
              <el-icon><Picture /></el-icon>
              <span>{{ $t('horse.avatar') }}</span>
            </div>
          </template>
          <div class="avatar-upload-section">
            <el-upload
              class="avatar-uploader"
              :http-request="customUpload"
              :before-upload="beforeAvatarUpload"
              :show-file-list="false"
              accept="image/*"
            >
              <ImagePreview 
                v-if="horseForm.avatar" 
                :src="horseForm.avatar" 
                width="150px" 
                height="150px"
                :enable-preview="true"
                fit="cover"
              />
              <el-icon v-else class="avatar-uploader-icon"><Plus /></el-icon>
            </el-upload>
            <el-button 
              v-if="horseForm.avatar" 
              type="danger" 
              size="small" 
              @click="handleAvatarDelete"
              style="margin-top: 10px;"
            >
              <el-icon><Delete /></el-icon>
              {{ $t('horse.deleteAvatar') }}
            </el-button>
          </div>
          <div v-if="uploading" class="uploading-text">
            {{ $t('horse.uploading') }}
          </div>
        </el-form-item>

        <!-- Introduction -->
        <el-form-item prop="remark">
          <template #label>
            <div class="form-label">
              <el-icon><Document /></el-icon>
              <span>{{ $t('horse.introduction') }}</span>
            </div>
          </template>
          <el-input 
            v-model="horseForm.remark" 
            type="textarea"
            :rows="4"
            :placeholder="$t('horse.remarkPlaceholder')"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>

        <!-- Action Buttons -->
        <div class="form-actions">
          <el-button class="cancel-btn" @click="goBack">
            {{ $t('common.cancel') }}
          </el-button>
          <el-button 
            type="primary" 
            class="save-btn" 
            @click="submitForm"
            :loading="submitting"
          >
            {{ $t('common.save') }}
          </el-button>
        </div>
      </el-form>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowLeft, Trophy, Edit, Flag, MagicStick, User, Male, Female, Timer, Calendar, Picture, Document, Plus, Delete } from '@element-plus/icons-vue'
import { createHorse } from '@/api/horse'
import { useHorseStore } from '@/stores/horse'
import ImagePreview from '@/components/ImagePreview.vue'
import { compressImage } from '@/utils/image-compressor'
import { useI18n } from 'vue-i18n'
import MTime from '@/utils/time-handler'

const { t } = useI18n()
const router = useRouter()
const horseStore = useHorseStore()
const formRef = ref(null)
const loading = ref(false)
const submitting = ref(false)
const uploading = ref(false)
const isUpdatingFromBirthDate = ref(false)
const isUpdatingFromAge = ref(false)

const horseForm = reactive({
  name: '',
  breed: '',
  color: '',
  age: null,
  gender: true,
  birthDate: '',
  remark: '',
  avatar: '',
  avatarFile: null,
  status: 1
})

const rules = {
  name: [
    { required: true, message: t('horse.nameRequired'), trigger: 'blur' },
    { min: 2, max: 50, message: t('horse.nameLength'), trigger: 'blur' }
  ],
  breed: [
    { required: true, message: t('horse.breedRequired'), trigger: 'blur' },
    { min: 2, max: 50, message: t('horse.breedLength'), trigger: 'blur' }
  ],
  age: [
    { required: true, message: t('horse.ageRequired'), trigger: 'blur' },
    { type: 'number', min: 0, max: 50, message: t('horse.ageRange'), trigger: 'blur' }
  ]
}

const calculateAgeFromBirthDate = (birthDate) => {
  if (!birthDate) return null
  const today = new Date()
  const birth = new Date(birthDate)
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return Math.max(0, age)
}

const calculateBirthDateFromAge = (age) => {
  if (age === null || age === undefined) return ''
  const today = new Date()
  const birthYear = today.getFullYear() - age
  const birthDate = new Date(birthYear, today.getMonth(), today.getDate())
  return birthDate.toISOString().split('T')[0]
}

const handleAgeChange = (newAge) => {
  if (isUpdatingFromBirthDate.value) return
  
  isUpdatingFromAge.value = true
  horseForm.birthDate = calculateBirthDateFromAge(newAge)
  setTimeout(() => {
    isUpdatingFromAge.value = false
  }, 100)
}

const handleBirthDateChange = (newBirthDate) => {
  if (isUpdatingFromAge.value) return
  
  isUpdatingFromBirthDate.value = true
  horseForm.age = calculateAgeFromBirthDate(newBirthDate)
  setTimeout(() => {
    isUpdatingFromBirthDate.value = false
  }, 100)
}

const submitForm = async () => {
  if (!formRef.value) return
  
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    
    submitting.value = true
    
    try {
      const formData = new FormData()
      formData.append('name', horseForm.name)
      formData.append('breed', horseForm.breed)
      formData.append('color', horseForm.color)
      formData.append('age', horseForm.age)
      formData.append('gender', horseForm.gender)
      formData.append('birthDate', horseForm.birthDate ? MTime.toIsoString(horseForm.birthDate) : '')
      formData.append('remark', horseForm.remark || '')
      
      if (horseForm.avatarFile) {
        formData.append('file', horseForm.avatarFile)
      }
      
      const res = await createHorse(formData)
      
      if (res.success) {
        ElMessage.success(t('horse.addSuccess'))
        router.push('/horse')
      } else {
        ElMessage.error(res.message || t('horse.addFailed'))
      }
    } catch (error) {
      console.error('Failed to add horse:', error)
      ElMessage.error(error.response?.data?.message || t('horse.addFailed'))
    } finally {
      submitting.value = false
    }
  })
}

const goBack = () => {
  router.back()
}

const beforeAvatarUpload = (file) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'

  if (!isJpgOrPng) {
    ElMessage.error(t('horse.avatarFormat'))
    return false
  }
  return true
}

const customUpload = async (options) => {
  const { file, onSuccess, onError } = options
  
  console.log('=== HORSE IMAGE UPLOAD START ===')
  console.log('1. Original file:', {
    name: file.name,
    type: file.type,
    size: file.size,
    sizeMB: (file.size / 1024 / 1024).toFixed(2) + 'MB'
  })
  
  try {
    uploading.value = true
    console.log('2. Starting compression...')
    
    const compressedFile = await compressImage(file, 0.17, 1920)
    console.log('3. Compression complete:', {
      name: compressedFile.name,
      type: compressedFile.type,
      size: compressedFile.size,
      sizeMB: (compressedFile.size / 1024 / 1024).toFixed(2) + 'MB'
    })
    
    horseForm.avatarFile = compressedFile
    horseForm.avatar = URL.createObjectURL(compressedFile)
    
    uploading.value = false
    console.log('4. File stored for upload with form')
    console.log('5. Preview URL created:', horseForm.avatar)
    
    ElMessage.success(t('horse.imageSelected'))
    onSuccess()
  } catch (error) {
    uploading.value = false
    console.error('=== IMAGE SELECTION ERROR ===')
    console.error('Error details:', error)
    console.error('Error message:', error.message)
    ElMessage.error(t('horse.imageProcessFailed') + ': ' + (error.message || 'Unknown error'))
    onError(error)
  }
}

const handleAvatarDelete = () => {
  if (!horseForm.avatar) return
  
  horseForm.avatar = ''
  horseForm.avatarFile = null
  ElMessage.success(t('horse.avatarRemoved'))
}
</script>

<style scoped lang="scss">
.add-horse-page {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 20px;

  .page-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;

    .back-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 14px;
      color: #606266;

      &:hover {
        color: #409eff;
      }
    }

    .page-title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: #303133;
    }
  }

  .form-card {
    max-width: 800px;
    margin: 0 auto;
    border-radius: 12px;

    :deep(.el-card__header) {
      padding: 20px 24px;
      border-bottom: 1px solid #f0f2f5;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #ffffff;
      border-radius: 12px 12px 0 0 !important;

      .card-header {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 16px;
        font-weight: 600;

        .el-icon {
          font-size: 20px;
        }
      }
    }

    :deep(.el-card__body) {
      padding: 32px 24px;
    }

    .horse-form {
      .el-form-item {
        margin-bottom: 24px;

        :deep(.el-form-item__content) {
          margin-left: 0 !important;
        }
      }

      .form-label {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 8px;
        font-size: 14px;
        font-weight: 500;
        color: #606266;

        .el-icon {
          font-size: 16px;
          color: #409eff;
        }

        .required {
          color: #f56c6c;
        }
      }

      .form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
        margin-bottom: 24px;

        @media (max-width: 768px) {
          grid-template-columns: 1fr;
          gap: 16px;
          margin-bottom: 20px;
        }
      }

      :deep(.el-radio-group) {
        display: flex;
        gap: 16px;

        @media (max-width: 480px) {
          flex-direction: column;
          gap: 8px;

          .el-radio {
            width: 100%;
            justify-content: center;
          }
        }

        .el-radio {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-right: 0;
          padding: 8px 16px;
          border: 1px solid #dcdfe6;
          border-radius: 6px;
          transition: all 0.3s ease;

          &:hover {
            border-color: #409eff;
            background-color: #f5f7fa;
          }

          &.is-checked {
            border-color: #409eff;
            background-color: #ecf5ff;
            color: #409eff;
            font-weight: 500;

            .el-radio__input {
              .el-radio__inner {
                background-color: #409eff;
                border-color: #409eff;
              }
            }
          }

          .el-icon {
            font-size: 16px;
          }
        }
      }

      :deep(.el-input-number) {
        width: 100%;

        .el-input-number__decrease,
        .el-input-number__increase {
          background-color: #f5f7fa;
          border-color: #dcdfe6;
          color: #606266;

          &:hover {
            color: #409eff;
          }
        }
      }

      .avatar-upload-section {
        margin-top: 8px;
      }
      
      .avatar-uploader {
        border: 1px dashed #d9d9d9;
        border-radius: 6px;
        cursor: pointer;
        position: relative;
        overflow: hidden;
        transition: all 0.3s;
        width: 120px;
        height: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #fafafa;
        
        :deep(.el-upload) {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
      }
      
      .avatar-uploader:hover {
        border-color: #409EFF;
      }
      
      .avatar-uploader-icon {
        font-size: 28px;
        color: #999;
        cursor: pointer;
      }
      
      .uploading-text {
        margin-top: 8px;
        color: #409EFF;
        font-size: 14px;
      }
      
      @media (max-width: 480px) {
        .avatar-uploader {
          width: 100px;
          height: 70px;
        }
      }

      :deep(.el-textarea) {
        .el-textarea__inner {
          resize: vertical;
        }

        .el-form-item__content {
          position: relative;
        }

        .word-limit {
          position: absolute;
          right: 12px;
          bottom: 8px;
          font-size: 12px;
          color: #909399;
        }
      }

      .form-actions {
        display: flex;
        gap: 12px;
        margin-top: 32px;
        padding-top: 24px;
        border-top: 1px solid #f0f2f5;

        @media (max-width: 480px) {
          flex-direction: column;
          gap: 8px;
          margin-top: 24px;
          padding-top: 16px;
        }

        .cancel-btn,
        .save-btn {
          flex: 1;
          height: 44px;
          font-size: 15px;
          font-weight: 500;
          border-radius: 8px;
          transition: all 0.3s ease;

          @media (max-width: 480px) {
            width: 100%;
            height: 48px;
          }
        }

        .cancel-btn {
          background-color: #ffffff;
          border-color: #dcdfe6;
          color: #606266;

          &:hover {
            border-color: #c0c4cc;
            color: #909399;
          }
        }

        .save-btn {
          &:not(:disabled) {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

            &:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
            }

            &:active {
              transform: translateY(0);
            }
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }
    }
  }

  // Mobile responsive styles
  @media (max-width: 768px) {
    padding: 12px;

    .page-header {
      margin-bottom: 16px;

      .page-title {
        font-size: 18px;
      }
    }

    .form-card {
      :deep(.el-card__header) {
        padding: 16px 20px;

        .card-header {
          font-size: 15px;

          .el-icon {
            font-size: 18px;
          }
        }
      }

      :deep(.el-card__body) {
        padding: 20px 16px;
      }

      .horse-form {
        .el-form-item {
          margin-bottom: 20px;
        }

        .form-label {
          font-size: 13px;
        }
      }
    }
  }

  // Small mobile devices
  @media (max-width: 480px) {
    padding: 8px;

    .page-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      margin-bottom: 12px;

      .page-title {
        font-size: 16px;
      }
    }

    .form-card {
      border-radius: 8px;

      :deep(.el-card__header) {
        padding: 12px 16px;
        border-radius: 8px 8px 0 0 !important;

        .card-header {
          font-size: 14px;

          .el-icon {
            font-size: 16px;
          }
        }
      }

      :deep(.el-card__body) {
        padding: 16px 12px;
      }

      .horse-form {
        .el-form-item {
          margin-bottom: 16px;
        }

        .form-label {
          font-size: 12px;
          margin-bottom: 6px;

          .el-icon {
            font-size: 14px;
          }
        }

        :deep(.el-input__inner) {
          height: 40px;
          font-size: 14px;
        }

        :deep(.el-date-editor) {
          width: 100% !important;
        }
      }
    }
  }
}
</style>
